import type { ReactNode, Ref } from 'react';
import {
    Box,
    FormControl,
    FormErrorMessage,
    FormHelperText,
    Input,
    SimpleGrid,
    Stack,
    Text,
} from '@chakra-ui/react';
import {
    Dropdown,
    SegmentedControl,
    TabbedSections,
    type TabbedSection,
    type TabbedSectionsHandle,
} from '../../../ui';
import type {
    InventoryBin,
    InventoryFormReferenceField,
    InventoryPartType,
    InventoryTag,
    InventoryUnit,
} from '../../../types/inventory';
import {
    INVENTORY_PART_TYPE_OPTIONS,
    formatInventoryUnitLabel,
} from '../../../utils/inventoryConstants';
import { inventoryFieldProps } from '../../../utils/inventoryFieldStyles';
import { tagListHasName } from '../../../utils/inventoryTags';
import type { TypeaheadOption } from '../../../components/inventory/InventoryTypeaheadField';
import InventorySupplierDropdown from '../../../components/inventory/InventorySupplierDropdown';
import InventoryAdditionalPartNumbersField from '../../../components/inventory/InventoryAdditionalPartNumbersField';
import RoNumberField from '../../../components/inventory/RoNumberField';
import InventoryBinsField from '../../../components/inventory/InventoryBinsField';
import QuantityInputField from '../../../components/inventory/QuantityInputField';
import UnitSelectField from '../../../components/inventory/UnitSelectField';
import CurrencyInput from '../../../components/inventory/CurrencyInput';
import InventoryTagsField from '../../../components/inventory/InventoryTagsField';
import FieldLabelWithBadge, { FieldLabel } from './FieldLabelWithBadge';
import {
    addItemFieldProps,
    type AddItemDetailsVariant,
    type AddItemFormState,
    type AddItemSectionId,
    type PrefillField,
} from './types';

const SUB_LABEL_PROPS = {
    fontSize: 'sm',
    fontWeight: 'semibold',
    color: 'fg.default',
    mb: 1.5,
} as const;

const PART_TYPE_OPTIONS = INVENTORY_PART_TYPE_OPTIONS.map((option) => ({
    value: option.value,
    label: option.label,
}));

const PhotoAndTagsSummary = ({
    tags,
    suggestedMake,
    hasPhoto,
}: {
    tags: Array<InventoryTag>;
    suggestedMake: string | null;
    hasPhoto: boolean;
}) => {
    const items = [...(hasPhoto ? ['Photo'] : []), ...tags.map((t) => t.name)];
    if (items.length === 0) {
        return (
            <Text textStyle="caption" color="fg.muted">
                Optional
            </Text>
        );
    }
    const makeTagged = suggestedMake != null && tagListHasName(tags, suggestedMake);
    return (
        <Text textStyle="caption" color="fg.default" noOfLines={1}>
            {items.join(', ')}
            {makeTagged && (
                <Text as="span" color="fg.muted">
                    {' '}
                    · {items.length > 1 ? `${suggestedMake} ` : ''}added from the vehicle make
                </Text>
            )}
        </Text>
    );
};

export interface SupplierFieldModel {
    display: string;
    options: TypeaheadOption[];
    onSearch: (query: string) => void;
    onSelect: (option: TypeaheadOption) => void;
    isLoading: boolean;
}

export interface ItemDetailsFormProps {
    variant: AddItemDetailsVariant;
    form: AddItemFormState;
    onFieldChange: <K extends keyof AddItemFormState>(key: K, value: AddItemFormState[K]) => void;
    onRestockAlertChange: (on: boolean) => void;
    prefilled: ReadonlySet<PrefillField>;
    fieldErrors: Partial<Record<InventoryFormReferenceField, string>>;
    additionalPartNumbers: Array<string>;
    onAdditionalPartNumbersChange: (value: Array<string>) => void;
    supplier: SupplierFieldModel;
    locationOptions: TypeaheadOption[];
    bins: Array<InventoryBin>;
    onBinsChange: (bins: Array<InventoryBin>) => void;
    allowedUnits: Array<InventoryUnit>;
    isUnitsLoading: boolean;
    isUnitsError: boolean;
    tags: Array<InventoryTag>;
    onTagsChange: (tags: Array<InventoryTag>) => void;
    suggestedMake: string | null;
    hasPhoto: boolean;
    /** Image upload field; the drawer owns its upload state. */
    imageField: ReactNode;
    /** Rendered above the sticky section tabs (the part summary card). */
    header?: ReactNode;
    sectionsRef?: Ref<TabbedSectionsHandle<AddItemSectionId>>;
}

const ItemDetailsForm = ({
    variant,
    form,
    onFieldChange,
    onRestockAlertChange,
    prefilled,
    fieldErrors,
    additionalPartNumbers,
    onAdditionalPartNumbersChange,
    supplier,
    locationOptions,
    bins,
    onBinsChange,
    allowedUnits,
    isUnitsLoading,
    isUnitsError,
    tags,
    onTagsChange,
    suggestedMake,
    hasPhoto,
    imageField,
    header,
    sectionsRef,
}: ItemDetailsFormProps) => {
    const prefillBadge = variant === 'existing' ? 'From inventory' : 'From catalog';
    const badgeFor = (field: PrefillField, label = prefillBadge) =>
        prefilled.has(field) ? label : null;

    const partDetails = (
        <Stack spacing={3.5}>
            <FormControl isRequired {...addItemFieldProps('name')}>
                <FieldLabelWithBadge badge={badgeFor('name')}>Name</FieldLabelWithBadge>
                <Input
                    size="sm"
                    value={form.name}
                    placeholder="e.g. Front bumper cover, primed"
                    onChange={(e) => onFieldChange('name', e.target.value)}
                    {...inventoryFieldProps}
                />
            </FormControl>

            <FormControl isRequired {...addItemFieldProps('partType')}>
                <FieldLabel>Part Type</FieldLabel>
                <Dropdown<InventoryPartType>
                    size="sm"
                    options={PART_TYPE_OPTIONS}
                    value={form.partType}
                    onChange={(value) => onFieldChange('partType', value)}
                    placeholder="Select part type"
                />
            </FormControl>

            <FormControl
                isRequired
                isInvalid={!!fieldErrors.supplierId}
                {...addItemFieldProps('supplierId')}
            >
                <FieldLabel>Supplier</FieldLabel>
                <InventorySupplierDropdown
                    value={form.supplierId}
                    displayLabel={supplier.display}
                    options={supplier.options}
                    onSearch={supplier.onSearch}
                    onSelect={supplier.onSelect}
                    isLoading={supplier.isLoading}
                />
                {fieldErrors.supplierId ? (
                    <FormErrorMessage>{fieldErrors.supplierId}</FormErrorMessage>
                ) : (
                    <FormHelperText color="fg.muted" fontSize="xs">
                        Who you buy this from. Needed so reorders and cost tracking have somewhere
                        to go.
                    </FormHelperText>
                )}
            </FormControl>

            <Stack direction={{ base: 'column', sm: 'row' }} spacing={3} align="flex-start">
                <Box flex={1} w="100%" minW={0}>
                    <FieldLabelWithBadge isOptional>Other Part Numbers</FieldLabelWithBadge>
                    <InventoryAdditionalPartNumbersField
                        size="sm"
                        value={additionalPartNumbers}
                        onChange={onAdditionalPartNumbersChange}
                        primaryPartNumber={form.manufacturerPartNumber}
                        label=""
                        placeholder="Superseded, A/M equivalents"
                    />
                </Box>
                <FormControl flex={1} w="100%" minW={0}>
                    <FieldLabelWithBadge isOptional>Origin RO</FieldLabelWithBadge>
                    <RoNumberField
                        value={form.origin}
                        onChange={(val) => onFieldChange('origin', val)}
                        placeholder="RO # it was ordered for"
                    />
                </FormControl>
            </Stack>
        </Stack>
    );

    const location = (
        <>
            <SimpleGrid columns={{ base: 1, sm: 2 }} spacingX={3} spacingY={3.5}>
                <FormControl
                    isRequired
                    isInvalid={!!fieldErrors.locationId}
                    {...addItemFieldProps('locationId')}
                >
                    <FieldLabel>Location</FieldLabel>
                    <Dropdown
                        size="sm"
                        searchable
                        options={locationOptions.map((option) => ({
                            value: option.value,
                            label: option.label,
                        }))}
                        value={form.locationId || null}
                        onChange={(value) => onFieldChange('locationId', value)}
                        placeholder="Select location"
                    />
                    <FormErrorMessage>{fieldErrors.locationId}</FormErrorMessage>
                </FormControl>
                <Box minW={0}>
                    <FieldLabelWithBadge isOptional>Bin</FieldLabelWithBadge>
                    <InventoryBinsField
                        size="sm"
                        value={bins}
                        onChange={onBinsChange}
                        locationId={form.locationId || null}
                        label=""
                        isDisabled={!form.locationId}
                        placeholder={
                            form.locationId ? 'Search or create a bin' : 'Select a location first'
                        }
                    />
                </Box>
                <FormControl isRequired {...addItemFieldProps('inStockQuantity')}>
                    <FieldLabel>In-Stock Quantity</FieldLabel>
                    <QuantityInputField
                        value={form.inStockQuantity}
                        onChange={(val) => onFieldChange('inStockQuantity', val ?? 0)}
                    />
                </FormControl>
                <FormControl
                    isRequired
                    isInvalid={!!fieldErrors.unit || isUnitsError}
                    {...addItemFieldProps('unit')}
                >
                    <FieldLabelWithBadge badge={badgeFor('unit', 'Inferred')} badgeTone="neutral">
                        Unit
                    </FieldLabelWithBadge>
                    <UnitSelectField
                        value={form.unit}
                        onChange={(val) => onFieldChange('unit', val)}
                        isDisabled={isUnitsLoading || isUnitsError}
                    />
                    {fieldErrors.unit && <FormErrorMessage>{fieldErrors.unit}</FormErrorMessage>}
                </FormControl>
            </SimpleGrid>
            {variant === 'noMatch' && allowedUnits.length > 0 && (
                <Text textStyle="caption" color="fg.muted" mt={2}>
                    Units: {allowedUnits.map((u) => formatInventoryUnitLabel(u)).join(', ')}.
                </Text>
            )}
        </>
    );

    const cost = (
        <>
            <SimpleGrid columns={{ base: 1, sm: 2 }} spacingX={3} spacingY={3.5}>
                <FormControl>
                    <FieldLabelWithBadge badge={badgeFor('unitCost')}>Unit Cost</FieldLabelWithBadge>
                    <CurrencyInput
                        value={form.unitCost}
                        onChange={(val) => onFieldChange('unitCost', val)}
                        useThousandsSeparator
                    />
                </FormControl>
                <FormControl>
                    <FieldLabel>Restock Alerts</FieldLabel>
                    <SegmentedControl
                        size="sm"
                        fullWidth
                        aria-label="Restock alerts"
                        items={[
                            { value: 'on', label: 'On' },
                            { value: 'off', label: 'Off' },
                        ]}
                        value={form.restockAlert ? 'on' : 'off'}
                        onChange={(next) => onRestockAlertChange(next === 'on')}
                    />
                </FormControl>
                {form.restockAlert && (
                    <>
                        <FormControl isRequired {...addItemFieldProps('reorderQuantity')}>
                            <FieldLabel>Reorder At</FieldLabel>
                            <QuantityInputField
                                value={form.reorderQuantity}
                                onChange={(val) => onFieldChange('reorderQuantity', val)}
                            />
                        </FormControl>
                        <FormControl isRequired {...addItemFieldProps('targetQuantity')}>
                            <FieldLabel>Restock To</FieldLabel>
                            <QuantityInputField
                                value={form.targetQuantity}
                                onChange={(val) => onFieldChange('targetQuantity', val)}
                            />
                        </FormControl>
                    </>
                )}
            </SimpleGrid>
            <Text textStyle="caption" color="fg.muted" mt={2}>
                Unit cost is required when quantity is above zero.{' '}
                {form.restockAlert
                    ? 'You’ll get a low-stock alert at the reorder point.'
                    : 'Turn alerts on to set a reorder point and a restock-to level.'}
            </Text>
        </>
    );

    const photoAndTags = (
        <>
            <InventoryTagsField value={tags} onChange={onTagsChange} labelProps={SUB_LABEL_PROPS} />
            <Box mt={3}>{imageField}</Box>
        </>
    );

    const sections: Array<TabbedSection<AddItemSectionId>> = [
        { id: 'details', tabLabel: 'Details', title: 'Part Details', content: partDetails },
        { id: 'location', tabLabel: 'Location', title: 'Where It Lives', content: location },
        { id: 'cost', tabLabel: 'Cost', title: 'Cost And Restocking', content: cost },
        {
            id: 'photos',
            tabLabel: 'Photos',
            title: 'Photo And Tags',
            summary: (
                <PhotoAndTagsSummary tags={tags} suggestedMake={suggestedMake} hasPhoto={hasPhoto} />
            ),
            content: photoAndTags,
        },
    ];

    return (
        <TabbedSections<AddItemSectionId>
            ref={sectionsRef}
            header={header}
            sections={sections}
            stickyProps={{ mx: -5, pt: 1 }}
            tabsProps={{ px: 5 }}
        />
    );
};

export default ItemDetailsForm;
